/**
 * Custom hook for managing the state of a new room creation session.
 * Handles contact selection and avatar selection for new rooms.
 */
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useMatrixClient } from '../context/MatrixContext';
import { useContactList } from './useContactList';
import { useAvatar } from '../context/AvatarContext';
import { generateInitialsAvatar } from '../utils/avatar';
import logger from '../utils/logger';

export const useRoomCreate = (options = {}) => {
  const {
    initialSelectedContacts = [],
    allowRemoveCurrentUser = false,
    minParticipantsSelected = 0, // minimum number of participants the UI allows you to deselect to
    maxSelections = null, // null = unlimited, number = max selections (with auto-replace)
    allowEmptyRoom = false,
  } = options;

  const client = useMatrixClient();
  const { contactList: contacts } = useContactList();
  const { getUserAvatar } = useAvatar();
  const myUserId = client?.getUserId();
  const currentUser = myUserId ? client?.getUser(myUserId) : null;

  const [selectedContactIds, setSelectedContactIds] = useState(
    initialSelectedContacts.map(contact => contact.id),
  );
  
  const [topic, setTopic] = useState('');
  
  // Track users not in contact list (e.g., from user directory search)
  const [externalUsers, setExternalUsers] = useState({}); // userId -> {id, name, avatar}

  // Avatar state: What option is currently selected
  const [selectedAvatarOption, setSelectedAvatarOption] = useState('default');
  const [selectedAvatarFile, setSelectedAvatarFile] = useState(null); // For custom file uploads

  // Get the actual contact objects for selected IDs
  // Include both contacts from contact list AND external users (from search)
  const selectedContacts = useMemo(() => {
    const knownContacts = contacts.filter(contact => selectedContactIds.includes(contact.id));
    
    // Add external users (not in contact list)
    const knownContactIds = new Set(knownContacts.map(c => c.id));
    const externalContacts = selectedContactIds
      .filter(id => !knownContactIds.has(id))
      .map(id => externalUsers[id])
      .filter(Boolean);
    
    return [...knownContacts, ...externalContacts];
  }, [selectedContactIds, contacts, externalUsers]);
  
  // Fetch profile info for selected users not in contact list
  useEffect(() => {
    if (!client) return;
    
    const knownContactIds = new Set(contacts.map(c => c.id));
    const unknownSelectedIds = selectedContactIds.filter(id => !knownContactIds.has(id) && !externalUsers[id]);
    
    if (unknownSelectedIds.length === 0) return;
    
    let isMounted = true;
    
    const fetchExternalUsers = async () => {
      const newExternalUsers = {};
      
      for (const userId of unknownSelectedIds) {
        try {
          // Get profile info from Matrix
          const profile = await client.getProfileInfo(userId);
          const displayName = profile.displayname || userId;
          
          // Get avatar
          let avatarUrl = generateInitialsAvatar(displayName);
          if (profile.avatar_url) {
            try {
              avatarUrl = await getUserAvatar(userId, displayName, 40);
            } catch (err) {
              logger.warn(`Failed to fetch avatar for ${userId}`, err);
            }
          }
          
          newExternalUsers[userId] = {
            id: userId,
            name: displayName,
            avatar: avatarUrl,
          };
        } catch (error) {
          logger.error(`Failed to fetch profile for ${userId}:`, error);
          // Create minimal entry with initials
          newExternalUsers[userId] = {
            id: userId,
            name: userId,
            avatar: generateInitialsAvatar(userId),
          };
        }
      }
      
      if (isMounted && Object.keys(newExternalUsers).length > 0) {
        setExternalUsers(prev => ({ ...prev, ...newExternalUsers }));
      }
    };
    
    fetchExternalUsers();
    
    return () => {
      isMounted = false;
    };
  }, [client, selectedContactIds, contacts, externalUsers, getUserAvatar]);

  // Get available contacts (excluding already selected ones)
  const availableContacts = useMemo(() => {
    return contacts.filter(
      contact => 
        contact.id !== myUserId && 
        !selectedContactIds.includes(contact.id),
    );
  }, [selectedContactIds, contacts, myUserId]);

  // Check if we can remove more contacts
  const canRemoveContact = useCallback((contactId) => {
    if (contactId === myUserId && !allowRemoveCurrentUser) {
      return false;
    }
    return selectedContactIds.length > minParticipantsSelected;
  }, [selectedContactIds.length, myUserId, allowRemoveCurrentUser, minParticipantsSelected]);

  // Toggle a contact's selection
  const toggleContact = useCallback((contactId) => {
    setSelectedContactIds(prev => {
      const isSelected = prev.includes(contactId);
      
      if (isSelected) {
        // Remove if allowed
        if (canRemoveContact(contactId)) {
          return prev.filter(id => id !== contactId);
        }
        return prev;
      } else {
        // Add new contact
        if (maxSelections !== null && prev.length >= maxSelections) {
          // Replace first contact with new one
          return [...prev.slice(1), contactId];
        }
        return [...prev, contactId];
      }
    });
  }, [canRemoveContact, maxSelections]);

  // Remove a contact
  const removeContact = useCallback((contactId) => {
    if (canRemoveContact(contactId)) {
      setSelectedContactIds(prev => prev.filter(id => id !== contactId));
    }
  }, [canRemoveContact]);

  // Reset to original state
  const resetToOriginal = useCallback(() => {
    setSelectedContactIds(initialSelectedContacts.map(contact => contact.id));
    setTopic(''); // Add this line
    // Reset avatar state to default
    setSelectedAvatarOption('default');
    setSelectedAvatarFile(null);
    // Clean up external users cache to prevent memory growth across multiple room creations
    setExternalUsers({});
  }, [initialSelectedContacts]);

  // Get default title based on selected contacts
  const getDefaultTitle = useCallback(() => {
    if (selectedContacts.length === 0) {
      return '';
    }
    if (selectedContacts.length === 1) {
      return selectedContacts[0].name;
    }
    // For multiple contacts, join names
    return selectedContacts.map(c => c.name).join(', ');
  }, [selectedContacts]);

  // Avatar selection functions
  const selectDefaultAvatar = useCallback(() => {
    setSelectedAvatarOption('default');
    setSelectedAvatarFile(null);
  }, []);

  const selectPresetAvatar = useCallback((presetPath) => {
    setSelectedAvatarOption(presetPath);
    setSelectedAvatarFile(null);
  }, []);

  const selectCustomAvatar = useCallback((file) => {
    setSelectedAvatarOption('custom');
    setSelectedAvatarFile(file);
  }, []);

  // Check if we can create a room (must have at least one other participant)
  const canCreate = useMemo(() => {
    return allowEmptyRoom || selectedContacts.length > 0;
  }, [selectedContacts.length, allowEmptyRoom]);

  return {
    // Contact selection state
    selectedContacts,
    availableContacts,
    selectedContactIds,
    externalUsers: Object.values(externalUsers), // Cached users not in contact list
    
    // Contact selection actions
    toggleContact,
    removeContact,
    resetToOriginal,
    canRemoveContact,
    getDefaultTitle,
    
    // Topic state
    topic, 
    setTopic,

    // Avatar state
    selectedAvatarOption,
    selectedAvatarFile,
    
    // Avatar actions
    selectDefaultAvatar,
    selectPresetAvatar,
    selectCustomAvatar,
    
    // Permissions (always true for room creation)
    canInvite: true,
    canRename: true,
    canRemove: true,
    canSetRoomAvatar: true,
    canEditTopic: true,
    
    // Validation
    canCreate, // True if at least one other user is selected
    
    // Metadata
    maxSelections,
    currentUser,
  };
};
